import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Three-step security-questions reset flow:
 *   1. Enter email
 *   2. Answer the two security questions returned by the backend
 *   3. Set a new password (sent in the same request as the answers so there's
 *      no interstitial "reset token" to leak)
 */
export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [questions, setQuestions] = useState([]); // ["q1 text","q2 text"]
  const [answers, setAnswers] = useState(["", ""]);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [softMessage, setSoftMessage] = useState(""); // for can_reset:false
  const nav = useNavigate();

  // STEP 1 -> fetch the user's security questions by email
  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSoftMessage("");
    try {
      const r = await api.post("/auth/forgot-password/questions", { email });
      if (r.data?.can_reset) {
        setQuestions(r.data.questions || []);
        setStep(2);
      } else {
        setSoftMessage(
          r.data?.message ||
            "We couldn't find security questions for that email.",
        );
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // STEP 2+3 -> submit answers + new password together
  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPwd !== confirmPwd) {
      setError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify", {
        email,
        answers,
        new_password: newPwd,
        confirm_password: confirmPwd,
      });
      setStep(4); // success
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          "Those answers don't match what we have on file.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--bg)" }}
    >
      <div className="w-full max-w-md pm-card p-8" data-testid="forgot-password-card">
        <Link
          to="/auth"
          className="inline-flex items-center gap-1.5 text-sm mb-6"
          style={{ color: "var(--text-muted)" }}
          data-testid="forgot-back-link"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        {step === 1 && (
          <>
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "var(--hl-blue)" }}
            >
              <ShieldCheck size={20} style={{ color: "var(--primary)" }} />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Reset your password</h1>
            <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              Enter the email you signed up with. We'll ask you the two
              security questions you chose when you created your account.
            </p>
            <form onSubmit={lookup} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-sm font-medium block mb-1.5">Email</label>
                <input
                  className="pm-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email"
                />
              </div>
              {softMessage && (
                <div
                  className="text-sm p-3 rounded-xl"
                  style={{ background: "var(--hl-yellow)", color: "var(--text)" }}
                  data-testid="forgot-soft-message"
                >
                  {softMessage}
                </div>
              )}
              {error && (
                <div
                  className="text-sm p-3 rounded-xl"
                  style={{ background: "#fdecea", color: "var(--weakness)" }}
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="pm-btn-primary w-full flex items-center justify-center gap-2"
                data-testid="forgot-submit-btn"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Continue
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "var(--hl-blue)" }}
            >
              <ShieldCheck size={20} style={{ color: "var(--primary)" }} />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Verify it's you</h1>
            <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              Answer your two security questions, then set a new password.
              Answers are case- and space-insensitive.
            </p>
            <form onSubmit={submitReset} className="space-y-4" data-testid="forgot-verify-form">
              {questions.map((q, i) => (
                <div key={i} data-testid={`forgot-question-${i + 1}`}>
                  <label className="text-sm font-medium block mb-1.5">{q}</label>
                  <input
                    className="pm-input"
                    type="text"
                    autoComplete="off"
                    required
                    value={answers[i] || ""}
                    onChange={(e) => {
                      const next = [...answers];
                      next[i] = e.target.value;
                      setAnswers(next);
                    }}
                    placeholder="Your answer"
                    data-testid={`forgot-answer-${i + 1}`}
                  />
                </div>
              ))}
              <div
                className="pt-3 border-t"
                style={{ borderColor: "var(--border)" }}
              >
                <label className="text-sm font-medium block mb-1.5">New password</label>
                <input
                  className="pm-input"
                  type="password"
                  minLength={6}
                  required
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="At least 6 characters"
                  data-testid="forgot-new-password"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Confirm new password</label>
                <input
                  className="pm-input"
                  type="password"
                  minLength={6}
                  required
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  placeholder="Repeat it"
                  data-testid="forgot-confirm-password"
                />
              </div>
              {error && (
                <div
                  className="text-sm p-3 rounded-xl"
                  style={{ background: "#fdecea", color: "var(--weakness)" }}
                  data-testid="forgot-verify-error"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="pm-btn-primary w-full flex items-center justify-center gap-2"
                data-testid="forgot-verify-submit"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Reset password
              </button>
            </form>
          </>
        )}

        {step === 4 && (
          <div data-testid="forgot-success">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "var(--hl-yellow)" }}
            >
              <ShieldCheck size={20} style={{ color: "var(--text)" }} />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">You're all set</h1>
            <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              Your password has been updated. Sign in with your new password
              to continue.
            </p>
            <button
              onClick={() => nav("/auth")}
              className="pm-btn-primary w-full"
              data-testid="forgot-return-btn"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
